import axios from 'axios';
import { FeatureCollection } from 'geojson';
import type { ComputedRef, MaybeRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, onUnmounted, ref, toValue, unref, watch } from 'vue';

import { useUid } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Shipment, ShipmentTrackingSummary } from '@/model';

interface UseMapResponse {
  isMapActionsEnabled: ComputedRef<boolean>;
  isMapExpanded: Ref<boolean>;
  mapId: ComputedRef<string>;
  onMapDetailsButtonClick: () => void;
  refreshRouteGeoJson: () => void;
  refreshStaticMapUrl: () => void;
  routeGeoJson: ComputedRef<Loadable<FeatureCollection>>;
  showDynamicMap: ComputedRef<boolean>;
  staticMapUrl: ComputedRef<Loadable<string>>;
}

interface UseMapOptions {
  showMap?: MaybeRef<boolean>;
}

const DEFAULT_OPTIONS: UseMapOptions = { showMap: true };

const MAP_POLL_INTERVAL = 2000;
const MAP_POLL_DURATION_MAX = 10_000;

type PolledStaticMapUrl = Loadable<string> & {
  pollInterval?: ReturnType<typeof setInterval>;
  pollTimeout?: ReturnType<typeof setTimeout>;
};

type PolledRouteGeoJson = Loadable<FeatureCollection> & {
  pollInterval?: ReturnType<typeof setInterval>;
  pollTimeout?: ReturnType<typeof setTimeout>;
};

export const useMap = (
  shipment: MaybeRefOrGetter<Shipment | ShipmentTrackingSummary | undefined>,
  options: UseMapOptions = {},
): UseMapResponse => {
  const { uid } = useUid('map');

  const opts: UseMapOptions = { ...DEFAULT_OPTIONS, ...options };
  const isMapExpanded = ref(false);
  const _showDynamicMap = ref(false);
  const _isMapActionsEnabled = ref(false);
  const _staticMapUrl: Ref<PolledStaticMapUrl> = ref({ loading: false });
  const _routeGeoJson: Ref<PolledRouteGeoJson> = ref({ loading: false });
  const appEl = ref(document.querySelector('#app'));
  const mapEl: Ref<Element | null> = ref(null);

  const mapId = computed(() => {
    const realShipment = toValue(shipment);
    return `${uid}-${realShipment?.id}`;
  });

  const showDynamicMap = computed(() => _showDynamicMap.value);
  const isMapActionsEnabled = computed(() => _isMapActionsEnabled.value);
  const staticMapUrl = computed(() => _staticMapUrl.value);
  const routeGeoJson = computed(() => _routeGeoJson.value);

  const stopStaticMapPolling = () => {
    clearInterval(_staticMapUrl.value.pollInterval);
    _staticMapUrl.value.pollInterval = undefined;
    clearTimeout(_staticMapUrl.value.pollTimeout);
    _staticMapUrl.value.pollTimeout = undefined;
  };

  const fallbackToDynamicMap = () => {
    stopStaticMapPolling();
    _staticMapUrl.value.loading = false;
    _showDynamicMap.value = true;
    _isMapActionsEnabled.value = false;
  };

  const startStaticMapPolling = (shipmentId: number) => {
    stopStaticMapPolling();

    _staticMapUrl.value.pollInterval = setInterval(
      () => loadStaticMapUrl(shipmentId),
      MAP_POLL_INTERVAL,
    );

    _staticMapUrl.value.pollTimeout = setTimeout(() => {
      console.warn(
        `Static map polling duration exceeded, shipmentId=${shipmentId}`,
      );
      fallbackToDynamicMap();
    }, MAP_POLL_DURATION_MAX);
  };

  const loadStaticMapUrl = async (shipmentId: number) => {
    try {
      const url = await api.staticMap.getStaticMapForShipment(shipmentId);

      if (url) {
        stopStaticMapPolling();
        _staticMapUrl.value.data = url;
        _staticMapUrl.value.loading = false;
        return;
      }

      if (!_staticMapUrl.value.pollInterval && _staticMapUrl.value.loading) {
        startStaticMapPolling(shipmentId);
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error loading static map. Reverting to dynamic map');
      console.error(e);
      fallbackToDynamicMap();
    }
  };

  const stopRouteGeoJsonPolling = () => {
    clearInterval(_routeGeoJson.value.pollInterval);
    _routeGeoJson.value.pollInterval = undefined;
    clearTimeout(_routeGeoJson.value.pollTimeout);
    _routeGeoJson.value.pollTimeout = undefined;
  };

  const loadRouteGeoJson = async (shipmentId: number) => {
    try {
      _routeGeoJson.value.loading = true;
      const geoJson =
        await api.routeGeoJson.getRouteGeoJsonForShipment(shipmentId);
      if (geoJson) {
        _routeGeoJson.value.data = geoJson;
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.warn(
        'Route GeoJSON not available, will fall back to dynamic route generation',
      );
    } finally {
      _routeGeoJson.value.loading = false;
    }
  };

  const loadRouteGeoJsonForPoll = async (shipmentId: number) => {
    try {
      const geoJson =
        await api.routeGeoJson.getRouteGeoJsonForShipment(shipmentId);

      const realShipment = toValue(shipment);
      const currentShipmentId = realShipment?.id;
      if (currentShipmentId !== shipmentId) {
        return;
      }

      if (geoJson) {
        _routeGeoJson.value.data = geoJson;
        stopRouteGeoJsonPolling();
        _routeGeoJson.value.loading = false;
      } else if (
        !_routeGeoJson.value.pollInterval &&
        _routeGeoJson.value.loading
      ) {
        startRouteGeoJsonPolling(shipmentId);
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.warn('Route GeoJSON refresh failed, stopping poll', e);
      stopRouteGeoJsonPolling();
      _routeGeoJson.value.loading = false;
    }
  };

  const startRouteGeoJsonPolling = (shipmentId: number) => {
    stopRouteGeoJsonPolling();

    _routeGeoJson.value.loading = true;
    _routeGeoJson.value.pollInterval = setInterval(
      () => loadRouteGeoJsonForPoll(shipmentId),
      MAP_POLL_INTERVAL,
    );
    _routeGeoJson.value.pollTimeout = setTimeout(() => {
      console.warn(
        `Route GeoJSON polling duration exceeded, shipmentId=${shipmentId}`,
      );
      stopRouteGeoJsonPolling();
      _routeGeoJson.value.loading = false;
    }, MAP_POLL_DURATION_MAX);
  };

  const getStaticMapData = async () => {
    const realShipment = toValue(shipment);
    if (realShipment?.id) {
      _staticMapUrl.value.loading = true;
      loadStaticMapUrl(realShipment.id);
      loadRouteGeoJson(realShipment.id);
    }
  };

  const refreshRouteGeoJson = () => {
    const realShipment = toValue(shipment);
    const shipmentId = realShipment?.id;
    if (shipmentId) {
      startRouteGeoJsonPolling(shipmentId);
    }
  };

  const refreshStaticMapUrl = () => {
    const shipmentId = toValue(shipment)?.id;
    if (shipmentId) {
      stopStaticMapPolling();
      _staticMapUrl.value.data = undefined;
      _staticMapUrl.value.loading = true;
      loadStaticMapUrl(shipmentId);
    }
  };

  const onMapDetailsButtonClick = () => {
    if (!_showDynamicMap.value) {
      _showDynamicMap.value = true;
    }

    isMapExpanded.value = !isMapExpanded.value;
    _isMapActionsEnabled.value = !_isMapActionsEnabled.value;
  };

  watch(
    () => toValue(shipment)?.id,
    (newVal) => {
      stopStaticMapPolling();
      stopRouteGeoJsonPolling();
      _staticMapUrl.value.data = undefined;
      _routeGeoJson.value.data = undefined;
      if (newVal && unref(opts.showMap)) {
        _showDynamicMap.value = false;
        _isMapActionsEnabled.value = false;
        getStaticMapData();
      }
    },
    { immediate: true },
  );

  watch(
    isMapExpanded,
    (newVal: boolean) => appEl.value?.classList?.toggle('map-expanded', newVal),
    { immediate: true },
  );

  watch(
    mapId,
    (newVal: string) => {
      mapEl.value = document.querySelector(`#${newVal}`);
    },
    { flush: 'post' },
  );

  onUnmounted(() => {
    stopStaticMapPolling();
    stopRouteGeoJsonPolling();
    appEl.value?.classList?.remove('map-expanded');
  });

  return {
    isMapActionsEnabled,
    isMapExpanded,
    mapId,
    onMapDetailsButtonClick,
    refreshRouteGeoJson,
    refreshStaticMapUrl,
    routeGeoJson,
    showDynamicMap,
    staticMapUrl,
  };
};
